import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { doctorPatientAPI } from '../services/doctorApi';
import { AppShell } from '../components/Layout';
import EmptyState from '../components/EmptyState';
import avatarTint from '../utils/avatarTint';
import Icon from '../components/Icon';
import { displayPatientCode } from '../utils/patientCode';

// Clinical risk vocabulary from the shared design system (index.css) — the
// same classes the navigator portal uses, so a triage level reads identically
// in both. Labels are doctor-facing wording; the styling is shared.
const PRIORITY_META = {
  HIGH:     { label: 'Critical', cls: 'badge-critical' },
  MODERATE: { label: 'Moderate', cls: 'badge-moderate' },
  LOW:      { label: 'Low',      cls: 'badge-low' },
  UNKNOWN:  { label: 'Unknown',  cls: 'badge-unknown' },
};

function initials(first = '', last = '') {
  return `${first[0] || ''}${last[0] || ''}`.toUpperCase();
}
function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: '2-digit',
  });
}

// Phone layout (max-width 768px) — see the "Doctor My Patients / Appointments
// lists — phone layout" block in index.css. Everything tagged mlist-* or
// dr-mlist-* is either restyled there or shown on only one side of the
// breakpoint (mlist-phone-only / mlist-desktop-only); the desktop table is
// untouched.
//
// The phone row's muted line: the same values the desktop columns show —
// age, last visit date, the patient code and the visit count — on one line.
function phoneDetail(p) {
  return [
    p.age != null ? `${p.age} yrs` : null,
    p.lastVisitDate ? fmtDate(p.lastVisitDate) : null,
    displayPatientCode(p.patientCode),
    `${p.visitCount} visit${p.visitCount !== 1 ? 's' : ''}`,
  ].filter(Boolean).join(' · ');
}

export default function DoctorPatientsPage() {
  const navigate  = useNavigate();
  const { user }  = useAuth();
  const { toast } = useToast();

  // Dynamic page title
  useEffect(() => {
    document.title = 'My Patients — Previu';
    return () => { document.title = 'Previu — Patient Management'; };
  }, []);

  const [patients,          setPatients]          = useState([]);
  const [loading,           setLoading]           = useState(true);
  const [error,             setError]             = useState(null);
  const [search,            setSearch]            = useState('');
  const [page,              setPage]              = useState(0);
  const [totalPages,        setTotalPages]        = useState(1);
  const [totalCount,        setTotalCount]        = useState(0);
  // Field names match PatientStatsDto on the server: total, highRisk,
  // pendingActions, recentUploads. They previously read thisMonth /
  // pendingFollowUp / highPriority — none of which the API returns — so three of
  // the four cards rendered `undefined`. Found while building the Analytics page
  // (DEF-029), which reads the same figures.
  const [stats,             setStats]             = useState({ total: 0, highRisk: 0, pendingActions: 0, recentUploads: 0 });
  const [triagePriorityFilter, setTriagePriorityFilter] = useState('ALL');
  const [statusFilter,      setStatusFilter]      = useState('ALL');

  const fetchPatients = useCallback(async (pageNum, q) => {
    setLoading(true);
    setError(null);
    try {
      const res = await doctorPatientAPI.list({
        doctorEmail: user?.email,
        page: pageNum,
        size: 10,
        search: q,
      });
      const d = res.data;
      setPatients(d.patients || []);
      setTotalPages(d.totalPages || 1);
      setTotalCount(d.totalElements || 0);
      if (d.stats) setStats(d.stats);
    } catch {
      setError('Could not load patients. Please try again.');
      toast.error('Could not load patients — check your connection and try again');
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  useEffect(() => { fetchPatients(0, ''); }, [fetchPatients]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { setPage(0); fetchPatients(0, search); }, 300);
    return () => clearTimeout(t);
  }, [search, fetchPatients]);

  const goToPage = (n) => { setPage(n); fetchPatients(n, search); };

  // Client-side filter by triage priority and status
  const filteredPatients = patients.filter(p => {
    const matchTriage = triagePriorityFilter === 'ALL' || p.triagePriority === triagePriorityFilter;
    const matchStatus = statusFilter === 'ALL' || p.status === statusFilter;
    return matchTriage && matchStatus;
  });

  return (
    <AppShell breadcrumbs={[{ label: 'My Patients' }]}>

      {/* ── Header — canonical .page-header, same as PatientListPage ──
             No page actions: the Filter and Add Patient buttons that used to
             sit here were never wired to anything (no onClick), and neither
             belongs to a doctor anyway — filtering is already done by the
             search + dropdowns in the toolbar below, and patients reach a
             doctor's cohort via a navigator, not by the doctor adding them. */}
      <div className="page-header mlist-header fade-in-up">
        <div className="mlist-header-text">
          <div className="page-title">My Patients</div>
          <div className="page-subtitle mlist-desktop-only">Manage and monitor patient health records</div>
          {(!loading || totalCount > 0) && (
            <div className="mlist-count mlist-phone-only">{totalCount} patient{totalCount !== 1 ? 's' : ''}</div>
          )}
        </div>
      </div>

      {/* ── Stats — canonical .stats-row/.stat-card with the shared icon tile.
             The caption slot under each value describes what the number counts.
             It used to hold a trend chip — '+12.5%', '-25%', '+35%', '+8.2%'
             next to 'vs last month' — and every one of those was a string
             literal. On a brand-new doctor with no patients it rendered
             '0  ↗ +12.5% vs last month', which is where this was spotted.
             Nothing stores a historical snapshot to compute a delta from, so
             there is no honest version of that chip; DoctorAnalyticsPage
             already declined to add one for the same reason. The other portals
             (PatientListPage, LabTestListPage) use this slot for a plain
             description of the figure, which is what it does here now.
             Phones: one bordered strip (mlist-stats), number above a short
             label, no icon or caption. ── */}
      <div className="stats-row mlist-stats dr-mlist-stats fade-in-up delay-1">
        {[
          {
            tint: 'stat-icon--blue',
            icon: (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            ),
            label: 'Total patients',
            short: 'Patients',
            value: stats.total,
            note: 'assigned to you',
          },
          {
            tint: 'stat-icon--red',
            icon: (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            ),
            label: 'Critical cases',
            short: 'Critical',
            critical: true,
            value: stats.highRisk,
            note: 'triaged critical',
          },
          {
            tint: 'stat-icon--green',
            icon: (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
              </svg>
            ),
            label: 'Uploads (30 days)',
            short: 'Uploads (30d)',
            value: stats.recentUploads,
            note: 'across your patients',
          },
          {
            tint: 'stat-icon--purple',
            icon: (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
              </svg>
            ),
            label: 'Pending actions',
            short: 'Pending',
            value: stats.pendingActions,
            note: 'open items, all your patients',
          },
        ].map((s) => (
          <div className={`stat-card${s.critical ? ' dr-mlist-stat-critical' : ''}`} key={s.label}>
            <div className="stat-card-head">
              <div className={`stat-icon ${s.tint}`}>{s.icon}</div>
            </div>
            <div className="stat-label">
              <span className="mlist-desktop-only">{s.label}</span>
              <span className="mlist-phone-only">{s.short}</span>
            </div>
            <div className="stat-value">{s.value}</div>
            <div className="stat-change">{s.note}</div>
          </div>
        ))}
      </div>

      {/* ── Toolbar — canonical .toolbar/.search-input/.form-select.
             Phones: the search on its own line, the two dropdowns sharing the
             next one (dr-mlist-selects is display: contents on desktop). ── */}
      <div className="toolbar mlist-toolbar dr-mlist-toolbar fade-in-up delay-2">
        <div className="search-input-wrap">
          <label className="mlist-sr-only" htmlFor="doctor-patient-search">Search patients</label>
          <span className="search-icon"><Icon name="search" size={15} /></span>
          <input
            id="doctor-patient-search"
            className="search-input"
            type="text"
            placeholder="Search by name, ID, or condition…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="dr-mlist-selects">
          <select
            className="form-select dr-mlist-select"
            value={triagePriorityFilter}
            onChange={e => setTriagePriorityFilter(e.target.value)}
            aria-label="Filter by triage priority"
          >
            <option value="ALL">Triage Priority</option>
            <option value="HIGH">Critical</option>
            <option value="MODERATE">Moderate</option>
            <option value="LOW">Low</option>
          </select>
          <select
            className="form-select dr-mlist-select"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            aria-label="Filter by patient status"
          >
            <option value="ALL">All Patients</option>
            <option value="ACTIVE">Active</option>
            <option value="FOLLOW_UP">Follow-up</option>
            <option value="LAB_PENDING">Lab Pending</option>
          </select>
        </div>
      </div>

      {/* ── Patient table — canonical .table-wrap + real <table>, matching
             PatientListPage. Columns: Patient · Age · Last visit · Condition
             · Triage · Actions.
             Phones (dr-mlist): one bordered card of rows — initials, the name
             over a muted "age · last visit · INV code · visits" line, the
             triage chip and a chevron; the name is a stretched link, so the
             whole row opens the patient. Age, Last visit and Condition cells
             are hidden there (Condition was already hidden on phones). ── */}
      {loading ? (
        <div className="table-wrap fade-in-up delay-3">
          <div className="loading-state">
            <div className="spinner" />
            Loading patients…
          </div>
        </div>
      ) : error ? (
        <div className="alert alert-error fade-in-up delay-3">
          {error}
          <button className="btn btn-ghost btn-sm" style={{ marginLeft: 12 }}
            onClick={() => fetchPatients(0, search)}>
            Retry
          </button>
        </div>
      ) : filteredPatients.length === 0 ? (
        <EmptyState
          icon="people"
          title={search || triagePriorityFilter !== 'ALL' || statusFilter !== 'ALL'
            ? 'No patients match your filters'
            : 'No patients yet'}
          message={search || triagePriorityFilter !== 'ALL' || statusFilter !== 'ALL'
            ? 'Try adjusting your search or filters.'
            : 'Patients appear here once a navigator schedules an appointment for them.'}
          action={search || triagePriorityFilter !== 'ALL' || statusFilter !== 'ALL'
            ? { label: 'Clear filters', onClick: () => { setSearch(''); setTriagePriorityFilter('ALL'); setStatusFilter('ALL'); } }
            : undefined}
        />
      ) : (
        <div className="table-wrap dr-mlist fade-in-up delay-3">
          <table>
            <thead>
              <tr>
                <th>Patient</th>
                <th>Age</th>
                <th>Last Visit</th>
                <th>Condition</th>
                <th>Triage</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPatients.map(p => {
                const priority = PRIORITY_META[p.triagePriority] || PRIORITY_META.UNKNOWN;
                const detail = phoneDetail(p);
                return (
                  <tr
                    key={p.id}
                    className="dr-mlist-row"
                    onClick={() => navigate(`/doctor/patients/${p.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="cell-stack dr-mlist-c-main">
                      <div className="mlist-desktop-only" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div className={`avatar ${avatarTint(p.firstName)}`} aria-hidden="true">
                          {initials(p.firstName, p.lastName)}
                        </div>
                        <div>
                          <div style={{ fontWeight: 700 }}>{p.firstName} {p.lastName}</div>
                          <div className="cell-sub mono">{displayPatientCode(p.patientCode) || '—'}</div>
                        </div>
                      </div>
                      {/* Same initials, 4 tints and name key as the navigator
                          list, so a patient's colour matches across portals. */}
                      <span className={`avatar mlist-avatar mlist-phone-only ${avatarTint(`${p.firstName}${p.lastName}`, 4)}`} aria-hidden="true">
                        {initials(p.firstName, p.lastName)}
                      </span>
                      {/* stopPropagation: the row's onClick would navigate a second time. */}
                      <Link
                        className="dr-mlist-name mlist-row-link mlist-phone-only"
                        to={`/doctor/patients/${p.id}`}
                        data-testid="doctor-patient-row-link"
                        onClick={e => e.stopPropagation()}
                      >
                        {p.firstName} {p.lastName}
                      </Link>
                      <div className="dr-mlist-sub mlist-phone-only" title={detail}>{detail}</div>
                    </td>
                    <td>{p.age != null ? `${p.age} yrs` : '—'}</td>
                    <td className="cell-stack">
                      <div>{fmtDate(p.lastVisitDate)}</div>
                      <div className="cell-sub">
                        {p.visitCount} visit{p.visitCount !== 1 ? 's' : ''}
                      </div>
                    </td>
                    <td>{p.primaryCondition || '—'}</td>
                    <td className="dr-mlist-c-triage">
                      <span className={`badge ${priority.cls}`}>{priority.label}</span>
                    </td>
                    <td className="dr-mlist-c-end">
                      {/* The row is clickable for mouse users; this button is the
                          keyboard and screen-reader path to the same place
                          (on phones, the name link is). */}
                      <button
                        className="btn btn-secondary btn-sm mlist-desktop-only"
                        onClick={e => { e.stopPropagation(); navigate(`/doctor/patients/${p.id}`); }}
                      >
                        View →
                      </button>
                      <span className="mlist-chevron mlist-phone-only" aria-hidden="true"><Icon name="chevronRight" size={18} /></span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Phones (mlist-footer): count on the left, ‹ current › on the
              right with 44px targets. The page buttons are real <button>s,
              so they already take focus and Enter/Space. */}
          <div className="table-footer mlist-footer">
            <span>Showing {filteredPatients.length} of {totalCount} patients</span>
            {totalPages > 1 && (
              <div className="pagination">
                <button className="page-btn" disabled={page === 0}
                  onClick={() => goToPage(page - 1)} aria-label="Previous page">‹</button>
                {[...Array(Math.min(totalPages, 5))].map((_, i) => (
                  <button key={i}
                    className={`page-btn mlist-page-num ${i === page ? 'active' : ''}`}
                    onClick={() => goToPage(i)}
                    aria-label={`Page ${i + 1}`}
                    aria-current={i === page ? 'page' : undefined}>{i + 1}</button>
                ))}
                <button className="page-btn" disabled={page >= totalPages - 1}
                  onClick={() => goToPage(page + 1)} aria-label="Next page">›</button>
              </div>
            )}
          </div>
        </div>
      )}
    </AppShell>
  );
}


